/**
 * A small CSS parser for widget code: enough structure to scope selectors and
 * rename keyframes without being fooled by strings, comments, url(...) or
 * nested blocks. It doesn't validate CSS; unknown text is kept as written.
 */

export type CssDeclaration = { type: 'declaration'; text: string };
export type CssStyleRule = { type: 'rule'; selector: string; children: CssNode[] };
export type CssAtRule = { type: 'atrule'; name: string; prelude: string; children: CssNode[] | null };
export type CssNode = CssDeclaration | CssStyleRule | CssAtRule;

const isSpace = (ch: string) => ch === ' ' || ch === '\n' || ch === '\t' || ch === '\r' || ch === '\f';

/** Index just past a string literal starting at `start` (which holds the quote). */
const skipString = (css: string, start: number): number => {
  const quote = css[start];
  let i = start + 1;
  while (i < css.length) {
    const ch = css[i];
    if (ch === '\\') { i += 2; continue; }
    if (ch === quote || ch === '\n') return i + 1;
    i++;
  }
  return i;
};

/** Index just past a comment starting at `start` ("/*"). */
const skipComment = (css: string, start: number): number => {
  const end = css.indexOf('*/', start + 2);
  return end === -1 ? css.length : end + 2;
};

/**
 * Scans from `start` to the first `stops` character at depth 0 (outside
 * strings, comments and brackets). Returns that index, or css.length.
 */
const scanTo = (css: string, start: number, stops: string): number => {
  let depth = 0;
  let i = start;
  while (i < css.length) {
    const ch = css[i];
    if (ch === '"' || ch === '\'') { i = skipString(css, i); continue; }
    if (ch === '/' && css[i + 1] === '*') { i = skipComment(css, i); continue; }
    if (ch === '\\') { i += 2; continue; }
    if (ch === '(' || ch === '[') depth++;
    else if ((ch === ')' || ch === ']') && depth > 0) depth--;
    else if (depth === 0 && stops.includes(ch)) return i;
    i++;
  }
  return i;
};

/** Index of the "}" matching the "{" at `open`, or css.length if unclosed. */
const matchBrace = (css: string, open: number): number => {
  let depth = 0;
  let i = open;
  while (i < css.length) {
    const ch = css[i];
    if (ch === '"' || ch === '\'') { i = skipString(css, i); continue; }
    if (ch === '/' && css[i + 1] === '*') { i = skipComment(css, i); continue; }
    if (ch === '\\') { i += 2; continue; }
    if (ch === '{') depth++;
    else if (ch === '}') { depth--; if (depth === 0) return i; }
    i++;
  }
  return css.length;
};

/** Removes comments outside strings. */
export const stripComments = (css: string): string => {
  let out = '';
  let i = 0;
  while (i < css.length) {
    const ch = css[i];
    if (ch === '"' || ch === '\'') { const end = skipString(css, i); out += css.slice(i, end); i = end; continue; }
    if (ch === '/' && css[i + 1] === '*') { i = skipComment(css, i); out += ' '; continue; }
    if (ch === '\\') { out += css.slice(i, i + 2); i += 2; continue; }
    out += ch;
    i++;
  }
  return out;
};

const parseBlock = (css: string, start: number, end: number): CssNode[] => {
  const nodes: CssNode[] = [];
  let i = start;
  while (i < end) {
    const ch = css[i];
    if (isSpace(ch) || ch === ';') { i++; continue; }
    if (ch === '}') { i++; continue; } // stray closer
    if (ch === '@') {
      let j = i + 1;
      while (j < end && /[a-zA-Z0-9_-]/.test(css[j])) j++;
      const name = css.slice(i + 1, j).toLowerCase();
      const stop = Math.min(scanTo(css, j, '{;}'), end);
      const prelude = css.slice(j, stop).trim();
      if (css[stop] === '{') {
        const close = Math.min(matchBrace(css, stop), end);
        nodes.push({ type: 'atrule', name, prelude, children: parseBlock(css, stop + 1, close) });
        i = close + 1;
      } else {
        nodes.push({ type: 'atrule', name, prelude, children: null });
        i = css[stop] === ';' ? stop + 1 : stop;
      }
      continue;
    }
    const stop = Math.min(scanTo(css, i, '{;}'), end);
    if (css[stop] === '{') {
      const close = Math.min(matchBrace(css, stop), end);
      nodes.push({ type: 'rule', selector: css.slice(i, stop).trim(), children: parseBlock(css, stop + 1, close) });
      i = close + 1;
    } else {
      const text = css.slice(i, stop).trim();
      if (text) nodes.push({ type: 'declaration', text });
      i = css[stop] === ';' ? stop + 1 : stop;
    }
  }
  return nodes;
};

/** Parses a stylesheet (comments removed) into nodes. */
export const parseCss = (css: string): CssNode[] => {
  const clean = stripComments(css || '');
  return parseBlock(clean, 0, clean.length);
};

const serializeNodes = (nodes: CssNode[], indent: string): string => nodes.map((node) => {
  if (node.type === 'declaration') return `${indent}${node.text};`;
  if (node.type === 'rule') {
    return `${indent}${node.selector} {\n${serializeNodes(node.children, `${indent}  `)}\n${indent}}`;
  }
  const head = `${indent}@${node.name}${node.prelude ? ` ${node.prelude}` : ''}`;
  if (node.children === null) return `${head};`;
  return `${head} {\n${serializeNodes(node.children, `${indent}  `)}\n${indent}}`;
}).join('\n');

export const serializeCss = (nodes: CssNode[]): string => serializeNodes(nodes, '');

/**
 * Splits on `separator` at depth 0, outside strings and brackets.
 * Used for selector lists and comma-separated values.
 */
export const splitTopLevel = (text: string, separator: string): string[] => {
  const parts: string[] = [];
  let depth = 0;
  let last = 0;
  let i = 0;
  while (i < text.length) {
    const ch = text[i];
    if (ch === '"' || ch === '\'') { i = skipString(text, i); continue; }
    if (ch === '\\') { i += 2; continue; }
    if (ch === '(' || ch === '[') depth++;
    else if ((ch === ')' || ch === ']') && depth > 0) depth--;
    else if (depth === 0 && ch === separator) { parts.push(text.slice(last, i)); last = i + 1; }
    i++;
  }
  parts.push(text.slice(last));
  return parts;
};

/** Splits a declaration into property and value; null for nested-rule text or garbage. */
export const splitDeclaration = (text: string): { property: string; value: string } | null => {
  const colon = scanTo(text, 0, ':');
  if (colon >= text.length) return null;
  const property = text.slice(0, colon).trim();
  if (!/^-{0,2}[a-zA-Z_][a-zA-Z0-9_-]*$/.test(property)) return null;
  return { property, value: text.slice(colon + 1).trim() };
};
